import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { ArrowLeft, Calendar, AlertTriangle, Sparkles } from "lucide-react";

interface FormData {
  leaveMode: "regular" | "comp-off";
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
  compOffId?: string;
}

interface CompOffCredit {
  _id: string;
  workDate: string;
  creditDays: number;
  expiryDate?: string;
  reason?: string;
  status?: string;
}

export default function ApplyLeave() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [availableLeaves, setAvailableLeaves] = useState<number>(0);
  const [availableCompOffs, setAvailableCompOffs] = useState<CompOffCredit[]>(
    []
  );
  const [formData, setFormData] = useState<FormData>({
    leaveMode: "regular",
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    compOffId: "",
  });

  useEffect(() => {
    fetchLeaveSummary();
    fetchAvailableCompOffs();
  }, []);

  const fetchLeaveSummary = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/my-leaves`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // 🆕 Use remainingPaidLeaves directly from DB
      setAvailableLeaves(response.data.summary?.remainingPaidLeaves || 0);
    } catch (error) {
      console.error("Error fetching leave summary:", error);
    }
  };

  const fetchAvailableCompOffs = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/available`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAvailableCompOffs(
        (response.data || []).map((item: CompOffCredit) => ({
          ...item,
        }))
      );
    } catch (error) {
      console.error("Error fetching comp-off credits:", error);
    }
  };

  const calculateDays = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end.getTime() < start.getTime()) return 0;
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setWarning("");
    setIsLoading(true);

    try {
      if (
        !formData.leaveMode ||
        !formData.startDate ||
        !formData.endDate ||
        !formData.reason ||
        (formData.leaveMode === "regular" && !formData.leaveType)
      ) {
        setError("Please fill in all required fields");
        return;
      }

      const numberOfDays = calculateDays();
      if (numberOfDays === 0) {
        setError("Please select a valid start and end date");
        return;
      }

      const selectedCompOff = availableCompOffs.find(
        (c) => c._id === formData.compOffId
      );

      const token = localStorage.getItem("authToken");
      let response;

      if (formData.leaveMode === "comp-off") {
        if (!formData.compOffId) {
          setError("Please select a comp-off credit to use");
          return;
        }

        if (
          selectedCompOff &&
          numberOfDays > (selectedCompOff.creditDays || 0)
        ) {
          setError(
            `Selected comp-off has ${selectedCompOff.creditDays} day(s). Please adjust dates or pick another credit.`
          );
          return;
        }

        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}api/leaves/apply`,
          {
            leaveMode: "comp-off",
            compOffId: formData.compOffId,
            startDate: formData.startDate,
            endDate: formData.endDate,
            reason: formData.reason,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}api/leaves/apply`,
          {
            ...formData,
            leaveMode: "regular",
            numberOfDays,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      // Check if there's a warning message about unpaid leave
      if (
        formData.leaveMode === "regular" &&
        response.data.message &&
        response.data.message.includes("UNPAID")
      ) {
        setWarning(response.data.message);
        setTimeout(() => {
          navigate("/employee/leaves", {
            state: {
              message:
                "Leave application submitted successfully (marked as unpaid due to insufficient paid leaves)!",
            },
          });
        }, 3000);
      } else {
        navigate("/employee/leaves", {
          state: { message: "Leave application submitted successfully!" },
        });
      }
    } catch (error: any) {
      console.error("Error applying leave:", error);
      setError(error.response?.data?.message || "Failed to apply for leave");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCompOff = availableCompOffs.find(
    (c) => c._id === formData.compOffId
  );
  const totalDays = calculateDays();
  const compOffCreditDays = selectedCompOff?.creditDays || 0;
  const isCompOffOverdrawn =
    formData.leaveMode === "comp-off" &&
    !!formData.compOffId &&
    totalDays > compOffCreditDays;

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/employee/leaves")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Apply for Leave</h2>
          <p className="text-muted-foreground">Submit your leave application</p>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {warning && (
        <div className="p-4 text-amber-600 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Important Notice</p>
            <p className="text-sm mt-1">{warning}</p>
          </div>
        </div>
      )}

      {/* Available Leaves Banner */}
      <Card
        className={`border-2 ${
          availableLeaves === 0
            ? "bg-red-50 border-red-300"
            : availableLeaves <= 5
            ? "bg-amber-50 border-amber-300"
            : "bg-blue-50 border-blue-200"
        }`}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar
                className={`h-8 w-8 ${
                  availableLeaves === 0
                    ? "text-red-600"
                    : availableLeaves <= 5
                    ? "text-amber-600"
                    : "text-blue-600"
                }`}
              />
              <div>
                <p className="text-sm text-muted-foreground">
                  Available Paid Leaves
                </p>
                <p
                  className={`text-3xl font-bold ${
                    availableLeaves === 0
                      ? "text-red-600"
                      : availableLeaves <= 5
                      ? "text-amber-600"
                      : "text-blue-600"
                  }`}
                >
                  {availableLeaves} days
                </p>
                {availableLeaves === 0 && (
                  <p className="text-xs text-red-600 font-medium mt-1">
                    ⚠️ No paid leaves remaining. New leaves will be unpaid.
                  </p>
                )}
                {availableLeaves > 0 && availableLeaves <= 5 && (
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    ⚠️ Running low on paid leaves
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 bg-purple-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  Available Comp-Off Credits
                </p>
                <p className="text-3xl font-bold text-purple-700">
                  {availableCompOffs.length} credit(s)
                </p>
                <p className="text-xs text-purple-700 font-medium mt-1">
                  Use approved comp-offs instead of paid leave
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/employee/compoff")}>
              Manage Comp-Offs
            </Button>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Leave Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3">
              <Label htmlFor="leaveMode">
                Leave Mode <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.leaveMode}
                onValueChange={(value: "regular" | "comp-off") => {
                  handleInputChange("leaveMode", value);
                  if (value === "comp-off") {
                    handleInputChange("leaveType", "Comp-Off");
                  } else {
                    handleInputChange("leaveType", "");
                    handleInputChange("compOffId", "");
                  }
                }}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular Leave</SelectItem>
                  <SelectItem value="comp-off">Use Comp-Off</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose regular leave to use paid balance, or comp-off to consume
                approved credits.
              </p>
            </div>

            <div className="grid gap-3">
              <Label htmlFor="leaveType">
                Leave Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.leaveType}
                onValueChange={(value) => handleInputChange("leaveType", value)}
                disabled={isLoading || formData.leaveMode === "comp-off"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Religious">Religious</SelectItem>
                  <SelectItem value="Sick Leave">Sick Leave</SelectItem>
                  <SelectItem value="Casual Leave">Casual Leave</SelectItem>
                  <SelectItem value="Vacation">Vacation</SelectItem>
                  <SelectItem value="Family">Family</SelectItem>
                  <SelectItem value="Travelling">Travelling</SelectItem>
                  <SelectItem value="Others">Others</SelectItem>
                  {formData.leaveMode === "comp-off" && (
                    <SelectItem value="Comp-Off">Comp-Off</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.leaveMode === "comp-off"
                  ? "Comp-off does not reduce paid leave balance"
                  : "All leave types count as paid leaves when balance is available"}
              </p>
            </div>

            {formData.leaveMode === "comp-off" && (
              <div className="grid gap-3">
                <Label htmlFor="compOffSelect">
                  Select Comp-Off Credit <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.compOffId}
                  onValueChange={(value) => handleInputChange("compOffId", value)}
                  disabled={isLoading || !availableCompOffs.length}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        availableCompOffs.length
                          ? "Choose approved comp-off"
                          : "No approved comp-off available"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCompOffs.map((credit) => (
                      <SelectItem key={credit._id} value={credit._id}>
                        {`${new Date(credit.workDate).toLocaleDateString()} • ${
                          credit.creditDays
                        } day(s)`}{" "}
                        {credit.expiryDate
                          ? `(Expires ${new Date(
                              credit.expiryDate
                            ).toLocaleDateString()})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only approved comp-off credits can be used.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-3">
                <Label htmlFor="startDate">
                  Start Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  min={getTodayDate()}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="grid gap-3">
                <Label htmlFor="endDate">
                  End Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                  min={formData.startDate || getTodayDate()}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {formData.startDate && formData.endDate && (
              <div
                className={`p-4 rounded-lg ${
                  formData.leaveMode === "comp-off"
                    ? isCompOffOverdrawn
                      ? "bg-red-50 border border-red-200"
                      : "bg-muted"
                    : totalDays > availableLeaves
                    ? "bg-red-50 border border-red-200"
                    : "bg-muted"
                }`}
              >
                <p className="text-sm font-medium">
                  Total Days:{" "}
                  <span className="text-lg font-bold">{totalDays}</span>
                </p>
                {formData.leaveMode === "comp-off" ? (
                  selectedCompOff ? (
                    isCompOffOverdrawn ? (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Selected comp-off has {compOffCreditDays} day(s). Adjust
                        dates or choose another credit.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        This leave will consume your comp-off credit earned on{" "}
                        {new Date(selectedCompOff.workDate).toLocaleDateString()}.
                      </p>
                    )
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Select a comp-off credit to continue.
                    </p>
                  )
                ) : (
                  <>
                    {totalDays > availableLeaves && availableLeaves > 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Warning: You only have {availableLeaves} paid leave(s)
                        remaining. This leave will be marked as UNPAID.
                      </p>
                    )}
                    {totalDays > availableLeaves && availableLeaves === 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Warning: No paid leaves available. This leave will be
                        marked as UNPAID.
                      </p>
                    )}
                    {totalDays <= availableLeaves && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Paid leave balance will be used for this request.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="grid gap-3">
              <Label htmlFor="reason">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="Please provide a reason for your leave"
                value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                disabled={isLoading}
                required
                rows={4}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/employee/leaves")}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="ellipsis" size={20} />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit Application"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
